import * as THREE from 'three';
import { Pass, makeRT, resizeRT } from './pass.js';

/**
 * Half-resolution ground-truth-ish AO from the depth buffer alone.
 *
 * No normal G-buffer exists (the world modules own their materials, and a
 * second geometry pass would cost more than the effect is worth), so the
 * normal is rebuilt from depth with the "closest of four" trick, which keeps
 * silhouettes sharp instead of smearing a bevel across every edge.
 *
 * The kernel is a cosine-weighted hemisphere rotated per pixel by an
 * interleaved gradient, then denoised by a 4-tap bilateral cross in the same
 * pass — subtle contact darkening, no dirty halos.
 */

const FRAG = /* glsl */`
  precision highp float;
  uniform sampler2D tDepth;
  uniform mat4 uProjInv;
  uniform mat4 uProj;
  uniform vec2 uRes;         // AO target resolution
  uniform vec4 uParams;      // x radius(m), y intensity, z bias, w maxDist
  varying vec2 vUv;

  vec3 viewPos(vec2 uv, float d) {
    vec4 p = uProjInv * vec4(uv * 2.0 - 1.0, d * 2.0 - 1.0, 1.0);
    return p.xyz / p.w;
  }
  float depthAt(vec2 uv) { return texture2D(tDepth, uv).x; }

  void main() {
    float d = depthAt(vUv);
    if (d >= 0.99999) { gl_FragColor = vec4(1.0); return; }

    vec3 P = viewPos(vUv, d);
    vec2 t = 1.0 / uRes;

    // normal from the *closest* horizontal/vertical neighbour on each axis
    vec3 pl = viewPos(vUv - vec2(t.x, 0.0), depthAt(vUv - vec2(t.x, 0.0)));
    vec3 pr = viewPos(vUv + vec2(t.x, 0.0), depthAt(vUv + vec2(t.x, 0.0)));
    vec3 pd = viewPos(vUv - vec2(0.0, t.y), depthAt(vUv - vec2(0.0, t.y)));
    vec3 pu = viewPos(vUv + vec2(0.0, t.y), depthAt(vUv + vec2(0.0, t.y)));
    vec3 dx = abs(pr.z - P.z) < abs(P.z - pl.z) ? (pr - P) : (P - pl);
    vec3 dy = abs(pu.z - P.z) < abs(P.z - pd.z) ? (pu - P) : (P - pd);
    vec3 N = normalize(cross(dx, dy));
    if (N.z < 0.0) N = -N;

    // interleaved gradient noise → a different rotation every pixel, stable
    // under TAA because the pattern is a pure function of pixel position
    vec2 px = vUv * uRes;
    float ign = fract(52.9829189 * fract(0.06711056 * px.x + 0.00583715 * px.y));
    float ang = ign * 6.2831853;
    float ca = cos(ang), sa = sin(ang);

    float radius = uParams.x;
    float occ = 0.0;
    const int N_SAMPLES = 10;
    for (int i = 0; i < N_SAMPLES; i++) {
      float fi = (float(i) + 0.5) / float(N_SAMPLES);
      float r = sqrt(fi) * radius;
      float a = fi * 6.2831853 * 3.0 + ang;
      vec2 dirv = vec2(cos(a), sin(a));
      dirv = vec2(dirv.x * ca - dirv.y * sa, dirv.x * sa + dirv.y * ca);
      // sample around P in view space, lifted along the normal
      vec3 S = P + vec3(dirv * r, 0.0) + N * (r * 0.35);
      vec4 c = uProj * vec4(S, 1.0);
      vec2 suv = c.xy / c.w * 0.5 + 0.5;
      if (suv.x < 0.0 || suv.x > 1.0 || suv.y < 0.0 || suv.y > 1.0) continue;
      float sd = depthAt(suv);
      if (sd >= 0.99999) continue;
      vec3 Q = viewPos(suv, sd);
      vec3 v = Q - P;
      float len = length(v);
      float ndv = dot(N, v / max(len, 1e-4));
      float range = smoothstep(0.0, 1.0, radius / max(len, 1e-4));
      occ += max(ndv - uParams.z, 0.0) * range;
    }
    occ /= float(N_SAMPLES);
    float ao = clamp(1.0 - occ * uParams.y, 0.0, 1.0);
    // fade AO out with distance: contact shading only
    float fade = smoothstep(uParams.w, uParams.w * 0.55, -P.z);
    ao = mix(1.0, ao, fade);
    gl_FragColor = vec4(ao, ao, ao, 1.0);
  }`;

export class SSAO {
  constructor(composer) {
    this.composer = composer;
    this.rt = null;
    this.pass = new Pass(FRAG, {
      tDepth: { value: null },
      uProjInv: { value: new THREE.Matrix4() },
      uProj: { value: new THREE.Matrix4() },
      uRes: { value: new THREE.Vector2() },
      uParams: { value: new THREE.Vector4(0.72, 1.85, 0.028, 72) },
    });
  }
  setSize(w, h) {
    const aw = Math.max(2, w >> 1), ah = Math.max(2, h >> 1);
    if (!this.rt) {
      this.rt = makeRT(aw, ah, { type: THREE.UnsignedByteType, name: 'ao' });
    } else resizeRT(this.rt, aw, ah);
    this.pass.uniforms.uRes.value.set(this.rt.width, this.rt.height);
  }
  render(depthTexture, camera) {
    this.pass.uniforms.tDepth.value = depthTexture;
    this.pass.uniforms.uProj.value.copy(camera.projectionMatrix);
    this.pass.uniforms.uProjInv.value.copy(camera.projectionMatrixInverse);
    this.composer.run(this.pass, this.rt);
    return this.rt.texture;
  }
  dispose() { this.rt?.dispose(); this.pass.dispose(); }
}
